import React from "react"
import { Analytics } from "@vercel/analytics/react"
import { Header, Footer } from "./LandingPage"
import { FileDown, Compass, Map, Award, LucideIcon } from "lucide-react"
import GradientBackground from './sections/GradientBackground'
import SEO from './common/SEO'

interface PlanProps {
  name: string;
  price: string;
  period: string;
  description: string;
}

interface PricingCardProps {
  plan: PlanProps;
  featured?: boolean;
}

interface FeatureCardProps {
  title: string;
  description: string;
  icon: LucideIcon;
  mockupImage: string;
}

const PricingCard: React.FC<PricingCardProps> = ({ plan, featured = false }) => {
  return (
    <div className={`flex flex-col p-8 bg-white border rounded-2xl shadow-lg ${featured ? 'ring-2 ring-[#1F80FF]' : ''}`}>
      <div className="mb-8">
        <h3 className="text-xl font-bold">{plan.name}</h3>
        <p className="mt-2 text-sm text-muted-foreground">{plan.description}</p>
        <div className="mt-4">
          <span className="text-3xl font-bold">${plan.price}</span>
          <span className="text-muted-foreground">/{plan.period}</span>
        </div>
      </div>

      <button 
        className={`mt-auto rounded-full px-8 py-3 text-white hover:bg-zinc-800 ${
          featured ? 'bg-[#1F80FF] hover:bg-[#1F80FF]/80' : 'bg-zinc-900'
        }`}
        onClick={() => window.open('https://apps.apple.com/us/app/currents-diving-app/id6504115639', '_blank')}
      >
        Get Pro Access
      </button>
    </div>
  )
}

const FeatureCard: React.FC<FeatureCardProps> = ({ title, description, icon: Icon, mockupImage }) => {
  return (
    <div className="bg-white rounded-3xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
      <div className="flex flex-col lg:flex-row h-full p-4 sm:p-6 lg:p-8 gap-6 lg:gap-8">
        <div className="flex-1 space-y-4 sm:space-y-6 w-full lg:w-1/2">
          <div className="w-12 h-12 bg-[#1F80FF]/10 rounded-xl flex items-center justify-center">
            <Icon className="w-6 h-6 text-[#1F80FF]" />
          </div>
          <h3 className="text-xl sm:text-2xl font-semibold">{title}</h3>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            {description}
          </p>
        </div>
        <div className="flex-1 flex items-center justify-center w-full lg:w-1/2">
          <img
            src={mockupImage}
            alt={title}
            className="w-auto h-[250px] sm:h-[350px] lg:h-[400px] object-contain"
          />
        </div>
      </div>
    </div>
  )
}

const ProPage: React.FC = () => {
  const features: FeatureCardProps[] = [
    {
      title: "One Simple Tap to Import Your Dives",
      description: "Connect your dive computer via Bluetooth and import your dive data in seconds. Compatible with Shearwater, Scubapro, Suunto, and more.",
      icon: FileDown,
      mockupImage: "/currents-divecomputer-mockup.png"
    },
    {
      title: "Discover Powerful Diving Insights",
      description: "Track your progress over time with detailed dive statistics. Monitor your SAC rate, depth profiles, and dive time trends.",
      icon: Compass,
      mockupImage: "/currents-statistics-mockup.png"
    },
    {
      title: "Live Import Progress",
      description: "Track your dive computer sync progress right from your lock screen. See how many dives have been imported without opening the app.",
      icon: Map,
      mockupImage: "/currents-live-activities-mockup.png"
    },
    {
      title: "Your Data, Your Control",
      description: "Export your dive logs in CSV format for backup or analysis. Keep your diving history safe and accessible.",
      icon: Award,
      mockupImage: "/currents-export-mockup.png"
    }
  ];

  return (
    <div className="flex min-h-screen flex-col relative">
      <SEO 
        path="/pro"
      />
      <Header />
      <main className="flex-1">
        {/* Hero Section */}
        <section className="container flex flex-col items-center justify-center space-y-4 pt-32 text-center">
          <div className="inline-block rounded-full bg-[#1F80FF]/10 px-3 py-1 text-sm text-[#1F80FF]">
            Currents <span className="ml-1 bg-[#1F80FF] text-white px-2 py-0.5 rounded-lg text-xs font-semibold">PRO</span>
          </div>
          <h1 className="text-4xl font-bold tracking-tighter sm:text-5xl md:text-6xl">
            Your Dive Data,<br />Your Way
          </h1>
          <p className="max-w-[600px] text-xl text-muted-foreground">
            Seamlessly sync your dive computer data, analyze your progress,
            and keep your dive history backed up and organized.
          </p>
        </section>

        {/* Updated Pricing Section */}
        <section className="container py-20 z-20">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold">Choose Your Plan</h2>
            <p className="mt-4 text-lg text-muted-foreground">
              Select the option that best fits your needs
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-[1200px] mx-auto">
            <PricingCard 
              plan={{
                name: "Monthly",
                price: "2.99",
                period: "month",
                description: "Perfect for trying out Pro features"
              }}
            />
            <PricingCard 
              plan={{
                name: "Yearly",
                price: "19.99",
                period: "year",
                description: "Most popular, save 44%"
              }}
              featured={true}
            />
            <PricingCard 
              plan={{
                name: "Lifetime",
                price: "29.99",
                period: "one-time",
                description: "Best value, unlimited access"
              }}
            />
          </div>
          <p className="text-sm text-muted-foreground text-center mt-8">
            Prices shown in USD. Local pricing available in the app.
          </p>
        </section>

        {/* Features Grid Section */}
        <section className="container py-12 sm:py-16 lg:py-20 bg-gray-50/50 rounded-3xl z-20">
          <div className="text-center mb-8 sm:mb-12 lg:mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold">Pro Features</h2>
            <p className="mt-4 text-base sm:text-lg text-muted-foreground">
              Everything you need to enhance your diving experience
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-8 max-w-[1200px] mx-auto px-2 sm:px-4">
            {features.map((feature, index) => (
              <FeatureCard key={index} {...feature} />
            ))}
          </div>
        </section>
      </main>
      <GradientBackground />
      <Footer />
      <Analytics />
    </div>
  )
}

export default ProPage